import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { colors, spacing, fontSize, fontWeight, radius } from '../styles/theme';
import { resolveFileUrl } from '../utils/fileUrl';
import { parseProofs } from '../utils/proofs';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000';

function getAuthHeader() {
  const token = localStorage.getItem('accessToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const fmtDate = (d?: string | null) =>
  d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '';

const fmtMoney = (n: number) =>
  '₹' + n.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

interface ExportColumn {
  id: string;
  label: string;
  width: number;
  numeric?: boolean;
  get: (t: any) => any;
}

const EXPORT_COLUMNS: ExportColumn[] = [
  { id: 'date',            label: 'Date',             width: 14, get: t => fmtDate(t.transaction_date) },
  { id: 'type',            label: 'Type',             width: 10, get: t => t.transaction_type || '' },
  { id: 'ledgerName',      label: 'Ledger Name',      width: 20, get: t => t.ledger_category_name || '' },
  { id: 'subledger1',      label: 'Subledger 1',      width: 18, get: t => t.ledger_subcategory_name || '' },
  { id: 'subledger2',      label: 'Subledger 2',      width: 18, get: t => t.ledger_subledger_2_name || '' },
  { id: 'subledger3',      label: 'Subledger 3',      width: 18, get: t => t.ledger_subledger_3_name || '' },
  // Subledger 4 and 5 are hidden at the client's request — see
  // VISIBLE_SUBLEDGER_SLOT_MAX in LedgerWorkspace, which hides the matching
  // columns on screen. The backend still stores these values and entries that
  // already carry them are untouched, so uncommenting these two lines (and
  // raising that constant to 5) is the whole of putting them back.
  // { id: 'subledger4',      label: 'Subledger 4',      width: 18, get: t => t.ledger_subledger_4_name || '' },
  // { id: 'subledger5',      label: 'Subledger 5',      width: 18, get: t => t.ledger_subledger_5_name || '' },
  // { id: 'particulars',     label: 'Particulars',      width: 28, get: t => t.particular || '' },
  { id: 'mode',            label: 'Mode',             width: 14, get: t => t.payment_mode || '' },
  { id: 'amount',          label: 'Amount (₹)',       width: 15, numeric: true, get: t => Number(t.amount) || 0 },
  { id: 'transferredFrom', label: 'Transferred From', width: 18, get: t => t.transferred_from_name || '' },
  { id: 'sender',          label: 'Sender',           width: 18, get: t => t.sender_name || '' },
  { id: 'receiver',        label: 'Receiver',         width: 18, get: t => t.receiver_name || '' },
  { id: 'enteredBy',       label: 'Entered By',       width: 22, get: t => t.entered_by_email || '' },
  { id: 'pettyCash',       label: 'Petty Cash',       width: 12, get: t => (t.paid_from_petty_cash ? 'Yes' : 'No') },
  // A real clickable hyperlink rather than a raw URL: the link is the point,
  // and a 100-character S3 address in a cell is unreadable. Kept (not dropped)
  // because it's the only evidence in the sheet that an expense was
  // substantiated — and it's untickable under Custom for anyone who wants it
  // gone. resolveFileUrl fixes up older rows that stored a backend-relative path.
  // An entry can carry up to 5 proofs but a cell holds one hyperlink, so link
  // the first and say how many there are.
  { id: 'proof',           label: 'Proof',            width: 10,
    get: t => {
      const proofs = parseProofs(t.proof);
      if (proofs.length === 0) return '';
      return {
        text: proofs.length > 1 ? `View (${proofs.length})` : 'View',
        hyperlink: resolveFileUrl(proofs[0]),
      };
    } },
  { id: 'remarks',         label: 'Remarks',          width: 28, get: t => t.remarks || '' },
];

const ALL_COLUMN_IDS = EXPORT_COLUMNS.map(c => c.id);
// The columns an accountant actually reads in a printed ledger.
const ESSENTIAL_COLUMN_IDS = ['date', 'ledgerName', 'subledger1', 'mode', 'amount'];

const PAYMENT_MODES = ['CASH', 'UPI', 'BANK_TRANSFER', 'CARD', 'OTHER'];

const NOT_RECORDED = '__none__';

type Period = 'month' | 'year' | 'custom';
type Preset = 'everything' | 'essentials' | 'custom';

interface Props {
  open: boolean;
  onClose: () => void;
  month: number;
  year: number;
  categories: { id: number; name: string }[];
  subcategories: { id: number; name: string }[];
}

const LedgerDownloadDialog: React.FC<Props> = ({ open, onClose, month, year, categories, subcategories }) => {
  const [period, setPeriod] = useState<Period>('month');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [preset, setPreset] = useState<Preset>('everything');
  const [busy, setBusy] = useState(false);

  // Rows for the chosen period. Fetched once per period change and reused for
  // both the live count and the download, so hitting Download never re-queries.
  const [rows, setRows] = useState<any[]>([]);
  const [loadingRows, setLoadingRows] = useState(false);

  const [cols, setCols] = useState<string[]>(ALL_COLUMN_IDS);
  const [pickedCategories, setPickedCategories] = useState<string[]>([]);
  const [pickedSubledgers, setPickedSubledgers] = useState<string[]>([]);
  const [pickedModes, setPickedModes] = useState<string[]>(PAYMENT_MODES);
  const [pickedUsers, setPickedUsers] = useState<string[]>([]);

  const allCategoryNames = useMemo(() => categories.map(c => c.name), [categories]);
  // Deduped by name: subledgers are scoped to a ledger, so the same name can
  // exist under several as separate rows. The export filter matches on
  // ledger_subcategory_name (the user wants every "Fuel" line, not one
  // ledger's row id), so one checkbox per distinct name is the right unit.
  const allSubledgerNames = useMemo(
    () => Array.from(new Set(subcategories.map(sc => sc.name))).sort(),
    [subcategories],
  );
  // Derived from the rows actually in range, so the list always matches the period.
  const allUsers = useMemo(() => {
    const set = new Set<string>();
    rows.forEach(t => set.add(t.entered_by_email || NOT_RECORDED));
    return Array.from(set).sort();
  }, [rows]);

  useEffect(() => {
    if (!open) return;
    setPeriod('month');
    setPreset('everything');
    setDateFrom(`${year}-${String(month).padStart(2, '0')}-01`);
    setDateTo(new Date(year, month, 0).toISOString().split('T')[0]);
    setCols(ALL_COLUMN_IDS);
    setPickedCategories(allCategoryNames);
    setPickedSubledgers(allSubledgerNames);
    setPickedModes(PAYMENT_MODES);
  }, [open, month, year, allCategoryNames, allSubledgerNames]);

  // Keep the user filter in sync with whatever the period actually contains.
  useEffect(() => { setPickedUsers(allUsers); }, [allUsers]);

  const fetchRows = useCallback(async () => {
    const params: Record<string, any> =
      period === 'month' ? { month, year }
      : period === 'year' ? { year }
      : { date_from: dateFrom, date_to: dateTo };
    if (period === 'custom' && (!dateFrom || !dateTo)) return;

    setLoadingRows(true);
    try {
      const res = await axios.get(`${API}/api/financial/transactions`, { params, headers: getAuthHeader() });
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error(err);
      setRows([]);
      toast.error('Could not load entries for that period');
    } finally {
      setLoadingRows(false);
    }
  }, [period, month, year, dateFrom, dateTo]);

  useEffect(() => { if (open) fetchRows(); }, [open, fetchRows]);

  const activeColumnIds =
    preset === 'everything' ? ALL_COLUMN_IDS
    : preset === 'essentials' ? ESSENTIAL_COLUMN_IDS
    : cols;

  // Row filters only bite under Custom, and only when a group is a strict
  // subset — a fully-ticked group means "no filter", which keeps the export
  // correct for values the dialog never listed.
  const selectedRows = useMemo(() => {
    if (preset !== 'custom') return rows;
    const narrows = (picked: string[], all: string[]) => picked.length < all.length;
    let out = rows;
    if (narrows(pickedCategories, allCategoryNames)) out = out.filter(t => pickedCategories.includes(t.ledger_category_name));
    if (narrows(pickedSubledgers, allSubledgerNames)) out = out.filter(t => pickedSubledgers.includes(t.ledger_subcategory_name));
    if (narrows(pickedModes, PAYMENT_MODES)) out = out.filter(t => pickedModes.includes(t.payment_mode));
    if (narrows(pickedUsers, allUsers)) out = out.filter(t => pickedUsers.includes(t.entered_by_email || NOT_RECORDED));
    return out;
  }, [preset, rows, pickedCategories, pickedSubledgers, pickedModes, pickedUsers, allCategoryNames, allSubledgerNames, allUsers]);

  const selectedTotal = useMemo(
    () => selectedRows.reduce((s, t) => s + (Number(t.amount) || 0), 0),
    [selectedRows]
  );

  const periodLabel =
    period === 'month' ? `${MONTH_NAMES[month - 1]} ${year}`
    : period === 'year' ? `${year}`
    : `${fmtDate(dateFrom)} — ${fmtDate(dateTo)}`;

  const handleDownload = async () => {
    if (activeColumnIds.length === 0) { toast.error('Pick at least one column'); return; }
    if (period === 'custom' && dateFrom > dateTo) { toast.error('"From" date is after "To" date'); return; }
    if (selectedRows.length === 0) { toast.error('Nothing matches those filters'); return; }

    setBusy(true);
    try {
      const data = [...selectedRows].sort(
        (a, b) => new Date(a.transaction_date).getTime() - new Date(b.transaction_date).getTime());
      const chosen = EXPORT_COLUMNS.filter(c => activeColumnIds.includes(c.id));

      const wb = new ExcelJS.Workbook();
      const ws = wb.addWorksheet('Ledger');
      // SL No. is always first — it numbers the export itself, so it isn't one
      // of the toggleable data columns.
      ws.columns = [
        { header: 'SL No.', key: 'sl', width: 8 },
        ...chosen.map(c => ({ header: c.label, key: c.id, width: c.width })),
      ];
      const lastCol = chosen.length + 1;

      ws.spliceRows(1, 0, [], [], []);
      ws.mergeCells(`A1:${ws.getColumn(lastCol).letter}1`);
      ws.getCell('A1').value = 'Ledger Workspace — Export';
      ws.getCell('A1').font = { bold: true, size: 14 };
      ws.mergeCells(`A2:${ws.getColumn(lastCol).letter}2`);
      ws.getCell('A2').value =
        `${periodLabel}  ·  ${data.length} entr${data.length === 1 ? 'y' : 'ies'}  ·  ${fmtMoney(selectedTotal)}`;
      ws.getCell('A2').font = { size: 10, color: { argb: 'FF64748B' } };

      const headerRow = ws.getRow(4);
      headerRow.values = ['SL No.', ...chosen.map(c => c.label)];
      headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      headerRow.alignment = { vertical: 'middle', horizontal: 'center' };
      headerRow.eachCell(cell => {
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF2563EB' } };
      });

      data.forEach((t, i) => {
        // SL No. carries the entry's stored ledger_no so the sheet and the
        // screen call the same entry by the same number; it falls back to the
        // row position for entries predating that column.
        const row = ws.addRow([t.ledger_no ?? i + 1, ...chosen.map(c => c.get(t))]);
        row.eachCell((cell, colNumber) => {
          cell.border = { bottom: { style: 'hair', color: { argb: 'FFE2E8F0' } } };
          const col = chosen[colNumber - 2];
          if (col?.numeric) {
            cell.numFmt = '#,##0.00';
            cell.alignment = { horizontal: 'right' };
          }
          // exceljs writes the link but not the styling, so it'd otherwise
          // render as plain black text and not look clickable.
          if (col?.id === 'proof' && cell.value) {
            cell.font = { color: { argb: 'FF2563EB' }, underline: true };
            cell.alignment = { horizontal: 'center' };
          }
        });
        if (i % 2 === 1) {
          row.eachCell(cell => {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF8FAFC' } };
          });
        }
      });

      const amountIdx = chosen.findIndex(c => c.id === 'amount');
      if (amountIdx !== -1) {
        const totalRow = ws.addRow([]);
        totalRow.getCell(1).value = 'Total';
        totalRow.getCell(amountIdx + 2).value = selectedTotal;
        totalRow.getCell(amountIdx + 2).numFmt = '#,##0.00';
        totalRow.font = { bold: true };
        totalRow.eachCell(cell => {
          cell.border = { top: { style: 'thin', color: { argb: 'FF94A3B8' } } };
        });
      }

      ws.views = [{ state: 'frozen', ySplit: 4 }];
      ws.autoFilter = { from: { row: 4, column: 1 }, to: { row: 4, column: lastCol } };

      const buf = await wb.xlsx.writeBuffer();
      const safe = periodLabel.replace(/[^\w]+/g, '-').replace(/^-|-$/g, '');
      saveAs(new Blob([buf]), `Ledger-${safe}.xlsx`);
      toast.success(`Downloaded ${data.length} entries`);
      onClose();
    } catch (err) {
      console.error(err);
      toast.error('Failed to build the download');
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  const toggle = (list: string[], set: (v: string[]) => void, v: string) =>
    set(list.includes(v) ? list.filter(x => x !== v) : [...list, v]);

  const filterSummary = () => {
    const bits: string[] = [];
    if (pickedCategories.length < allCategoryNames.length) bits.push(`${pickedCategories.length} ledger names`);
    if (pickedSubledgers.length < allSubledgerNames.length) bits.push(`${pickedSubledgers.length} subledgers`);
    if (pickedModes.length < PAYMENT_MODES.length) bits.push(`${pickedModes.length} modes`);
    if (pickedUsers.length < allUsers.length) bits.push(`${pickedUsers.length} users`);
    return bits.length ? bits.join(', ') : 'no filters';
  };

  return (
    <div style={overlay}>
      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <div style={{ fontSize: fontSize.lg, fontWeight: fontWeight.bold, color: colors.textPrimary }}>Download Ledger</div>
          <button onClick={onClose} style={closeBtn}>&times;</button>
        </div>

        {/* Period */}
        <div style={{ display: 'flex', alignItems: 'center', gap: spacing(3), marginTop: spacing(4) }}>
          <label style={fieldLabel}>Period</label>
          <select value={period} onChange={e => setPeriod(e.target.value as Period)} style={{ ...input, flex: 1 }}>
            <option value="month">{MONTH_NAMES[month - 1]} {year}</option>
            <option value="year">Whole year {year}</option>
            <option value="custom">Custom range…</option>
          </select>
        </div>
        {period === 'custom' && (
          <div style={{ display: 'flex', gap: spacing(2), alignItems: 'center', marginTop: spacing(2), paddingLeft: 64 }}>
            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={input} />
            <span style={{ color: colors.textFaint, fontSize: fontSize.sm }}>to</span>
            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} style={input} />
          </div>
        )}

        {/* Live count */}
        <div style={countBar}>
          {loadingRows ? (
            <span style={{ color: colors.textMuted }}>Counting…</span>
          ) : (
            <>
              <strong style={{ color: colors.textPrimary }}>{selectedRows.length}</strong>
              <span style={{ color: colors.textMuted }}>
                {selectedRows.length === 1 ? ' entry' : ' entries'}
                {preset === 'custom' && selectedRows.length !== rows.length && ` of ${rows.length}`}
              </span>
              <span style={{ color: colors.textFaint }}> · </span>
              <strong style={{ color: colors.textPrimary, fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(selectedTotal)}</strong>
              <span style={{ color: colors.textFaint }}> · {activeColumnIds.length} columns</span>
            </>
          )}
        </div>

        {/* Presets */}
        <div style={{ marginTop: spacing(4), display: 'flex', flexDirection: 'column', gap: spacing(2) }}>
          <PresetRow
            on={preset === 'everything'} onClick={() => setPreset('everything')}
            title="Everything" hint={`All ${ALL_COLUMN_IDS.length} columns, every entry in the period`} />
          <PresetRow
            on={preset === 'essentials'} onClick={() => setPreset('essentials')}
            title="Essentials" hint="Date, Ledger Name, Subledger 1, Mode, Amount" />
          <PresetRow
            on={preset === 'custom'} onClick={() => setPreset('custom')}
            title="Custom…" hint={preset === 'custom' ? `${cols.length} columns · ${filterSummary()}` : 'Choose columns and filter rows'} />
        </div>

        {/* Custom detail — only rendered when actually chosen */}
        {preset === 'custom' && (
          <div style={customPanel}>
            <Group title="Columns" summary={`${cols.length} of ${ALL_COLUMN_IDS.length}`} defaultOpen
              onAll={() => setCols(ALL_COLUMN_IDS)} onNone={() => setCols([])}>
              <div style={checkGrid}>
                {EXPORT_COLUMNS.map(c => (
                  <Check key={c.id} label={c.label} on={cols.includes(c.id)} onChange={() => toggle(cols, setCols, c.id)} />
                ))}
              </div>
            </Group>

            <Group title="Ledger names" summary={pickedCategories.length === allCategoryNames.length ? 'all' : `${pickedCategories.length} of ${allCategoryNames.length}`}
              onAll={() => setPickedCategories(allCategoryNames)} onNone={() => setPickedCategories([])}>
              <div style={checkGrid}>
                {categories.map(c => (
                  <Check key={c.id} label={c.name} on={pickedCategories.includes(c.name)} onChange={() => toggle(pickedCategories, setPickedCategories, c.name)} />
                ))}
              </div>
            </Group>

            <Group title="Subledger 1" summary={pickedSubledgers.length === allSubledgerNames.length ? 'all' : `${pickedSubledgers.length} of ${allSubledgerNames.length}`}
              onAll={() => setPickedSubledgers(allSubledgerNames)} onNone={() => setPickedSubledgers([])}>
              <div style={checkGrid}>
                {allSubledgerNames.map(name => (
                  <Check key={name} label={name} on={pickedSubledgers.includes(name)} onChange={() => toggle(pickedSubledgers, setPickedSubledgers, name)} />
                ))}
              </div>
            </Group>

            <Group title="Payment modes" summary={pickedModes.length === PAYMENT_MODES.length ? 'all' : `${pickedModes.length} of ${PAYMENT_MODES.length}`}
              onAll={() => setPickedModes(PAYMENT_MODES)} onNone={() => setPickedModes([])}>
              <div style={checkGrid}>
                {PAYMENT_MODES.map(m => (
                  <Check key={m} label={m.replace('_', ' ')} on={pickedModes.includes(m)} onChange={() => toggle(pickedModes, setPickedModes, m)} />
                ))}
              </div>
            </Group>

            <Group title="Entered by" summary={pickedUsers.length === allUsers.length ? 'all' : `${pickedUsers.length} of ${allUsers.length}`}
              onAll={() => setPickedUsers(allUsers)} onNone={() => setPickedUsers([])}>
              <div style={checkGrid}>
                {allUsers.length === 0
                  ? <span style={{ fontSize: fontSize.sm, color: colors.textFaint, fontStyle: 'italic' }}>No entries in this period.</span>
                  : allUsers.map(u => (
                    <Check key={u} label={u === NOT_RECORDED ? 'Not recorded' : u} on={pickedUsers.includes(u)} onChange={() => toggle(pickedUsers, setPickedUsers, u)} />
                  ))}
              </div>
            </Group>
          </div>
        )}

        <div style={footer}>
          <button onClick={onClose} style={{ ...btn, background: colors.surface, border: `1px solid ${colors.border}`, color: colors.textSecondary }}>Cancel</button>
          <button
            onClick={handleDownload}
            disabled={busy || loadingRows || activeColumnIds.length === 0 || selectedRows.length === 0}
            style={{
              ...btn, background: colors.primary, border: 'none', color: '#fff',
              opacity: busy || loadingRows || activeColumnIds.length === 0 || selectedRows.length === 0 ? 0.5 : 1,
            }}
          >
            {busy ? 'Preparing…' : 'Download .xlsx'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Bits ────────────────────────────────────────────────────────────────────

const PresetRow: React.FC<{ on: boolean; onClick: () => void; title: string; hint: string }> =
  ({ on, onClick, title, hint }) => (
    <button type="button" onClick={onClick} style={{
      display: 'flex', alignItems: 'flex-start', gap: spacing(3), width: '100%', textAlign: 'left',
      padding: `${spacing(3)} ${spacing(3)}`, cursor: 'pointer', borderRadius: radius.md,
      border: `1px solid ${on ? colors.primary : colors.border}`,
      background: on ? colors.primaryBg : colors.surface,
    }}>
      <span style={{
        width: 16, height: 16, borderRadius: '50%', flexShrink: 0, marginTop: 2,
        border: `2px solid ${on ? colors.primary : colors.border}`,
        boxShadow: on ? `inset 0 0 0 3px ${colors.surface}` : 'none',
        background: on ? colors.primary : colors.surface,
      }} />
      <span>
        <span style={{ display: 'block', fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: on ? colors.primaryDark : colors.textPrimary }}>{title}</span>
        <span style={{ display: 'block', fontSize: fontSize.xs, color: colors.textMuted, marginTop: 2 }}>{hint}</span>
      </span>
    </button>
  );

const Group: React.FC<{
  title: string; summary: string; defaultOpen?: boolean;
  onAll: () => void; onNone: () => void; children: React.ReactNode;
}> = ({ title, summary, defaultOpen = false, onAll, onNone, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ borderBottom: `1px solid ${colors.borderMuted}` }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: `${spacing(2)} 0` }}>
        <button type="button" onClick={() => setOpen(o => !o)} style={{
          background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex',
          alignItems: 'center', gap: spacing(2), fontSize: fontSize.sm, color: colors.textSecondary, fontWeight: fontWeight.semibold,
        }}>
          <span style={{ display: 'inline-block', width: 10, color: colors.textFaint, transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .12s' }}>›</span>
          {title}
          <span style={{ color: colors.textFaint, fontWeight: fontWeight.medium }}>{summary}</span>
        </button>
        {open && (
          <span style={{ display: 'flex', gap: spacing(2) }}>
            <button type="button" onClick={onAll} style={linkBtn}>All</button>
            <button type="button" onClick={onNone} style={linkBtn}>None</button>
          </span>
        )}
      </div>
      {open && <div style={{ paddingBottom: spacing(3) }}>{children}</div>}
    </div>
  );
};

const Check: React.FC<{ label: string; on: boolean; onChange: () => void }> = ({ label, on, onChange }) => (
  <label style={{
    display: 'flex', alignItems: 'center', gap: spacing(2), cursor: 'pointer',
    fontSize: fontSize.sm, color: on ? colors.textSecondary : colors.textMuted,
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  }}>
    <input type="checkbox" checked={on} onChange={onChange} style={{ cursor: 'pointer', accentColor: colors.primary, flexShrink: 0 }} />
    {label}
  </label>
);

// ── Styles ──────────────────────────────────────────────────────────────────

const overlay: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', zIndex: 10001,
  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: spacing(4),
};
const card: React.CSSProperties = {
  background: colors.surface, padding: '1.5rem', borderRadius: radius.xl,
  width: '520px', maxWidth: '94vw', maxHeight: '88vh', overflowY: 'auto',
  boxShadow: '0 20px 40px rgba(0,0,0,0.25)', boxSizing: 'border-box',
};
const countBar: React.CSSProperties = {
  marginTop: spacing(4), padding: `${spacing(3)} ${spacing(3)}`,
  background: colors.surfaceMuted, borderRadius: radius.md, fontSize: fontSize.md,
};
const customPanel: React.CSSProperties = {
  marginTop: spacing(3), padding: `0 ${spacing(3)}`,
  border: `1px solid ${colors.borderMuted}`, borderRadius: radius.md,
};
const checkGrid: React.CSSProperties = {
  display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
  gap: `${spacing(2)} ${spacing(3)}`, maxHeight: 180, overflowY: 'auto',
};
const footer: React.CSSProperties = {
  display: 'flex', gap: spacing(2), justifyContent: 'flex-end',
  marginTop: spacing(4), paddingTop: spacing(4), borderTop: `1px solid ${colors.borderMuted}`,
};
const btn: React.CSSProperties = {
  padding: '0.55rem 1.1rem', borderRadius: radius.sm, fontSize: fontSize.md,
  fontWeight: fontWeight.semibold, cursor: 'pointer',
};
const linkBtn: React.CSSProperties = {
  background: 'none', border: 'none', color: colors.primary,
  cursor: 'pointer', fontSize: fontSize.xs, fontWeight: fontWeight.semibold, padding: 0,
};
const input: React.CSSProperties = {
  padding: '0.45rem 0.6rem', borderRadius: radius.sm,
  border: `1px solid ${colors.border}`, fontSize: fontSize.md,
  color: colors.textSecondary, background: colors.surface,
};
const fieldLabel: React.CSSProperties = {
  fontSize: fontSize.sm, fontWeight: fontWeight.semibold,
  color: colors.textSecondary, width: 52, flexShrink: 0,
};
const closeBtn: React.CSSProperties = {
  background: 'none', border: 'none', fontSize: '1.4rem',
  cursor: 'pointer', color: colors.textMuted, lineHeight: 1,
};

export default LedgerDownloadDialog;
